import immutableStore from 'alt-utils/lib/ImmutableUtil';
import immutable from 'immutable';
import alt from 'components/Dispatcher';
import SharingActions from 'actions/sharing/SharingActions';
import SharingFormActions from 'actions/sharing/SharingFormActions';

/**
 * `FileUploadSharingDialogFormStore` stores client-side info regarding a potential
 * call sharing before it's submitted to the server. It performs basic data validation on
 * the client-side when the user wants to submit the "Share Call" form
 */
@immutableStore
class FileUploadSharingDialogFormStore {
  constructor() {
    this.state = this.initialState();
    this.bindActions(SharingActions);
    this.bindActions(SharingFormActions);
  }

  initialState() {
    return immutable.Map({
      comments: '',
      contentToShare: {value: 'fullCall', label: 'Full call'},
      expireAccess: false,
      includeContentFromOthers: false,
      isFullCall: true,
      hasHighlights: false,
      password: null,
      passwordProtected: false,
      selectedHighlights: [],
      // sharingTargets is a list of user_names_or_emails that is passed to the backend
      sharingTargets: [],
      // sharingValues is a list of {label: 'label', value: 'value'} that is used by <Select /> in
      // <FileUploadSharingDialogFormSharingTargets />
      sharingValues: [],
      willExpireAccess: null,
      shareSubmitAttempted: false,
      requestFeedback: false,
    });
  }

  /**
   * Reset `this.state` to `this.initialState()` since the user has closed `DirectCallSharingDialog`
   */
  onReset() {
    this.setState(this.initialState());
  }

  /**
   * Method from SharingActions, we want to know if the user has clicked on the "Share" button at least once,
   * because we only want to show errors after the such action (it is annoying to show errors if the user has
   * not tried to submit yet).
   * P.S. Don't remove the arg fileUpload even though it is not used, alt.js is picky and checks a function
   * of the right name and with the right number of arguments before using it as a callback.
   */
  onShareFileUpload() {
    const state = this.state.set('shareSubmitAttempted', true);
    this.setState(state);
  }

  /**
   * Update this.state.get('comments')
   * @param  {string} comments Comments to update
   */
  onUpdateComments(comments) {
    const state = this.state.set('comments', comments);
    this.setState(state);
  }

  /**
   * Update this.state.get('contentToShare')
   * @param  {Object} value An object of format {value: 'value', label: 'label'}
   */
  onUpdateContentToShare(value) {
    const state = this.state.set('contentToShare', value);
    this.setState(state);
  }

  /**
   * Update this.state.get('includeContentFromOthers')
   * @param  {Boolean} isInputChecked true if the "Include content from others" checkbox is checked;
   *                                  false otherwise
   */
  onUpdateIncludeContentFromOthers(isInputChecked) {
    const state = this.state.set('includeContentFromOthers', isInputChecked);
    this.setState(state);
  }

  onUpdateRequestFeedback(requestFeedback) {
    const state = this.state.set('requestFeedback', requestFeedback);
    this.setState(state);
  }

  /**
   * Update this.state.get('sharingTargets')
   * @param  {array} sharingTargets An array of user ids or emails
   */
  onUpdateSharingTargets(sharingTargets) {
    const state = this.state.set('sharingTargets', sharingTargets);
    this.setState(state);
    this.preventDefault();
  }

  /**
   * Update this.state.get('expireAccess')
   * @param  {Boolean} isInputChecked true if the "Expire access" checkbox is checked;
   *                                  false otherwise
   */
  onUpdateExpireAccess(isInputChecked) {
    const state = this.state.set('expireAccess', isInputChecked);
    this.setState(state);
  }

  /**
   * Update this.state.get('sharingValues')
   * @param  {array} sharingValues An array of {label: 'label', value: 'value'} objects
   */
  onUpdateSharingValues(sharingValues) {
    const state = this.state.set('sharingValues', sharingValues);
    this.setState(state);
  }

  /**
   * Update this.state.get('willExpireAccess')
   * @param  {Date} date The expire date selected by the user
   */
  onUpdateWillExpireAccess(date) {
    const state = this.state.set('willExpireAccess', date);
    this.setState(state);
  }

  /**
   * Update this.state.get('password')
   * @param  {string} password The password supplied by the user
   */
  onUpdatePassword(password) {
    const state = this.state.set('password', password);
    this.setState(state);
  }

  /**
   * Update this.state.get('passwordProtected')
   * @param  {Boolean} isInputChecked true if the "Password protection" checkbox is checked;
   *                                  false othersie
   */
  onUpdatePasswordProtection(isInputChecked) {
    const state = this.state.set('passwordProtected', isInputChecked);
    this.setState(state);
  }

  /**
   * Update this.state.get('selectedHighlights') using `values`
   * @param  {array} values An array of {value: 'value': label: 'label'} objects
   */
  onUpdateSelectedHighlights(values) {
    const state = this.state.set('selectedHighlights', values);
    this.setState(state);
  }
}

export default alt.createStore(
  FileUploadSharingDialogFormStore,
  'FileUploadSharingDialogFormStore'
);
